import React from "react";
import { Button } from "reactstrap";
import { formatCurrency, extractRecordId, formatDateReadable, formatDatePeriod } from "./helpers";

// Re-export formatDatePeriod for backward compatibility
export { formatDatePeriod };

/**
 * Maps revenue data from API response to component state format
 * @param {Object} data - Revenue data from API
 * @returns {Object} Mapped revenue data
 */
export const mapRevenueData = (data) => ({
    totalRevenue: parseFloat((data.totalRevenue ?? data.total_revenue) || 0),
    campaignRevenue: parseFloat((data.campaignRevenue ?? data.campaign_revenue) || 0),
    smartDiscountRevenue: parseFloat((data.smartDiscountRevenue ?? data.smart_discount_revenue) || 0),
    veeperROI: parseFloat((data.veeperROI ?? data.veeper_roi) || 0),
    recoveredRevenue: parseFloat((data.recoveredRevenue ?? data.recovered_revenue) || 0),
    uniqueCouponBlocked: parseInt((data.uniqueCouponBlocked ?? data.unique_coupon_blocked) || 0),
    totalCouponsBlocked: parseInt((data.totalCouponsBlocked ?? data.total_coupons_blocked) || 0),
    totalOrderProtected: parseInt((data.totalOrderProtected ?? data.total_order_protected) || 0),
    ratioOfOrdersProtected: parseFloat((data.ratioOfOrdersProtected ?? data.ratio_of_orders_protected) || 0),
    recoveredRevenuePerOrder: parseFloat((data.recoveredRevenuePerOrder ?? data.recovered_revenue_per_order) || 0),
    revenueFromProtectedOrders: parseFloat((data.revenueFromProtectedOrders ?? data.revenue_from_protected_orders) || 0),
});

/**
 * Transforms a single coupon item for table display
 * @param {Object} coupon - Coupon object from API
 * @param {Function} onToggle - Toggle handler function
 * @returns {Object} Transformed coupon object
 */
export const transformCouponItem = (coupon, onToggle) => {
    const fullId = coupon.id;
    const displayId = extractRecordId(fullId);

    return {
        id: displayId,
        fullId: fullId, // Store full ID for API calls
        title: coupon.codeDiscount.title,
        status: coupon.codeDiscount.status,
        button: coupon.codeDiscount.status === "ACTIVE"
            ? <Button color="danger" size='sm' onClick={() => onToggle(fullId, 'deactive')}>Disable</Button>
            : <Button color="success" size='sm' onClick={() => onToggle(fullId, 'active')}>Enable</Button>,
    };
};

/**
 * Transforms array of coupons into DataTable format
 * @param {Array} coupons - Array of coupon objects from API
 * @param {Array} columns - Table column configuration
 * @param {Function} onToggle - Toggle handler function
 * @returns {Object} DataTable formatted object with columns and rows
 */
export const transformCouponsToTableData = (coupons, columns, onToggle) => {
    const rows = coupons.map(coupon => transformCouponItem(coupon, onToggle));

    return {
        columns,
        rows
    };
};

/**
 * Transforms a single blocked order item for table display
 * @param {Object} order - Blocked order object from API
 * @returns {Object} Transformed order object
 */
export const transformBlockedOrderItem = (order) => {
    const { order_no, revenue, created_at, total, coupon, extensions, applied_coupons } = order;

    const isOnBCL = applied_coupons
        ?.split(',')
        .map(ele => ele.toLowerCase().trim())
        .includes(coupon?.toLowerCase().trim())
        ? 'Yes'
        : 'No';

    return {
        order_no,
        recovered_revenue: formatCurrency(revenue),
        created_at: formatDateReadable(created_at),
        total: formatCurrency(total),
        coupon,
        applied_coupons,
        is_on_bcl: isOnBCL,
        extensions: extensions?.map(ele => ele)?.join(', ') || "",
    };
};

/**
 * Transforms array of blocked orders into DataTable format
 * @param {Array} orders - Array of blocked order objects from API
 * @param {Array} columns - Table column configuration
 * @returns {Object} DataTable formatted object with columns and rows
 */
export const transformBlockedOrdersToTableData = (orders, columns) => {
    const rows = orders.map(order => transformBlockedOrderItem(order));

    return {
        columns,
        rows
    };
};

